import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogFooter,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Label } from '@/components/shadcn/label'
import { Input } from '@/components/shadcn/input'
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/shadcn/select'
import {
  useGetApiAdminFeatureFlagsNames,
  useGetApiCompanies,
  usePostApiAdminFeatureFlags,
  useGetApiAdministrationAdministratorusers,
  useGetApiCompaniesCompanyIdMembersWithRoles,
  getGetApiAdminFeatureFlagsQueryKey,
} from '@/api/generated/administrator-user/administrator-user'
import { notify } from '@/lib/notify'
import { useMemo } from 'react'
import {FeatureFlagScope} from "@/api/model";

type Scope = typeof FeatureFlagScope[keyof typeof FeatureFlagScope]
type UserType = 'companyUser' | 'admin'

type FormValues = {
  featureName: string
  scope: Scope
  companyId: string
  userType: UserType
  userCompanyId: string
  selectedUserId: string
  userSearch: string
}

const defaultValues: FormValues = {
  featureName: '',
  scope: FeatureFlagScope.Global,
  companyId: '',
  userType: 'companyUser',
  userCompanyId: '',
  selectedUserId: '',
  userSearch: '',
}

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateFeatureFlagDialog({ isOpen, onOpenChange }: Props) {
  const { t } = useTranslation('super-admin-feature-flags')
  const queryClient = useQueryClient()

  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>({ defaultValues })

  const scope = watch('scope')
  const userType = watch('userType')
  const userCompanyId = watch('userCompanyId')
  const userSearch = watch('userSearch')
  const selectedUserId = watch('selectedUserId')
  const featureName = watch('featureName')

  useEffect(() => {
    if (isOpen) reset(defaultValues)
  }, [isOpen])

  const { data: companiesData } = useGetApiCompanies()
  const { data: featureNamesData } = useGetApiAdminFeatureFlagsNames()
  const { data: adminUsersData } = useGetApiAdministrationAdministratorusers()
  const { data: companyMembersData } = useGetApiCompaniesCompanyIdMembersWithRoles(
    Number(userCompanyId),
    undefined,
    { query: { enabled: !!userCompanyId } }
  )

  const companies = companiesData?.data ?? []
  const knownFeatureFlags = featureNamesData?.data ?? []
  const adminUsers = adminUsersData?.data ?? []
  const companyMembers = companyMembersData?.data ?? []

  const filteredMembers = useMemo(() => {
    if (!userSearch) return companyMembers
    const lower = userSearch.toLowerCase()
    return companyMembers.filter(
      (m) =>
        m.firstName?.toLowerCase().includes(lower) ||
        m.lastName?.toLowerCase().includes(lower)
    )
  }, [companyMembers, userSearch])

  const { mutate: createFlag, isPending } = usePostApiAdminFeatureFlags({
    mutation: {
      onSuccess: () => {
        notify.success(t('toasts.created'))
        onOpenChange(false)
        queryClient.invalidateQueries({ queryKey: getGetApiAdminFeatureFlagsQueryKey() })
      },
      onError: (error) => notify.apiError(error, t('toasts.createError')),
    },
  })

  const onValid = (values: FormValues) => {
    const payload = {
      featureName: values.featureName,
      companyId: values.scope === FeatureFlagScope.Company ? Number(values.companyId) : null,
      userId: values.scope === FeatureFlagScope.User ? values.selectedUserId : null,
    }
    createFlag({ data: payload })
  }

  const isSubmitDisabled =
    isPending ||
    !featureName ||
    (scope === FeatureFlagScope.Company && !watch('companyId')) ||
    (scope === FeatureFlagScope.User && !selectedUserId)

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('dialog.title')}</DialogTitle>
        </DialogHeader>

        <form
          id="create-feature-flag-form"
          onSubmit={handleSubmit(onValid)}
          className="flex flex-col gap-4 flex-1 overflow-y-auto px-1 py-2 scrollbar-custom"
        >
          <div className="space-y-2">
            <Label>{t('dialog.fields.featureName')}</Label>
            <Select
              value={knownFeatureFlags.includes(featureName) ? featureName : '__custom__'}
              onValueChange={(val) => setValue('featureName', val === '__custom__' ? '' : val)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('dialog.fields.featureNamePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {knownFeatureFlags.map((flag) => (
                  <SelectItem key={flag} value={flag}>{flag}</SelectItem>
                ))}
                <SelectItem value="__custom__">{t('dialog.fields.customFeatureName')}</SelectItem>
              </SelectContent>
            </Select>

            {(!knownFeatureFlags.includes(featureName) || featureName === '') && (
              <Input
                placeholder={t('dialog.fields.featureNameCustomPlaceholder')}
                {...register('featureName')}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('dialog.fields.scope')}</Label>
            <Select
              value={scope}
              onValueChange={(val) => {
                setValue('scope', val as Scope)
                setValue('companyId', '')
                setValue('userType', 'companyUser')
                setValue('userCompanyId', '')
                setValue('userSearch', '')
                setValue('selectedUserId', '')
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={FeatureFlagScope.Global}>{t('dialog.scope.global')}</SelectItem>
                <SelectItem value={FeatureFlagScope.Company}>{t('dialog.scope.company')}</SelectItem>
                <SelectItem value={FeatureFlagScope.User}>{t('dialog.scope.user')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scope === FeatureFlagScope.Company && (
            <div className="space-y-2">
              <Label>{t('dialog.fields.company')}</Label>
              <Select value={watch('companyId')} onValueChange={(val) => setValue('companyId', val)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('dialog.fields.companyPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {scope === FeatureFlagScope.User && (
            <>
              <div className="space-y-2">
                <Label>{t('dialog.fields.userType')}</Label>
                <Select
                  value={userType}
                  onValueChange={(val) => {
                    setValue('userType', val as UserType)
                    setValue('userCompanyId', '')
                    setValue('userSearch', '')
                    setValue('selectedUserId', '')
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="companyUser">{t('dialog.userType.companyUser')}</SelectItem>
                    <SelectItem value="admin">{t('dialog.userType.admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {userType === 'admin' && (
                <div className="space-y-2">
                  <Label>{t('dialog.fields.user')}</Label>
                  <Select value={selectedUserId} onValueChange={(val) => setValue('selectedUserId', val)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('dialog.fields.userPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {adminUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id!}>
                          {u.firstName} {u.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {userType === 'companyUser' && (
                <>
                  <div className="space-y-2">
                    <Label>{t('dialog.fields.company')}</Label>
                    <Select
                      value={userCompanyId}
                      onValueChange={(val) => {
                        setValue('userCompanyId', val)
                        setValue('userSearch', '')
                        setValue('selectedUserId', '')
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('dialog.fields.companyPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {userCompanyId && (
                    <div className="space-y-2">
                      <Label>{t('dialog.fields.user')}</Label>
                      <Input
                        placeholder={t('dialog.fields.userSearchPlaceholder')}
                        value={userSearch}
                        onChange={(e) => {
                          setValue('userSearch', e.target.value)
                          setValue('selectedUserId', '')
                        }}
                      />
                      {filteredMembers.length > 0 && !selectedUserId && (
                        <div className="border rounded-md max-h-48 overflow-y-auto">
                          {filteredMembers.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex justify-between items-center"
                              onClick={() => {
                                setValue('selectedUserId', m.id!)
                                setValue('userSearch', `${m.firstName} ${m.lastName}`)
                              }}
                            >
                              <span>{m.firstName} {m.lastName}</span>
                              <span className="text-xs text-muted-foreground">{m.role}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="submit" form="create-feature-flag-form" disabled={isSubmitDisabled}>
            {isPending ? t('actions.saving') : t('actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
